// ═══════════════════════════════════════════════════════════════
// V·IANet Flow — GovernanceNode: 90×38, navy fill, shield icon
// M2: Fuente uniforme T.font.nodeTitle, sin descripción
// M4: Edición inline con doble clic (data.editing)
// M8: NodeResizer controlado por data.resizeEnabled
// ═══════════════════════════════════════════════════════════════

import { memo, useState, useCallback, useRef, useEffect } from 'react';
import { Handle, Position, NodeResizer, type NodeProps, useReactFlow } from '@xyflow/react';
import { FLOW_TOKENS as T } from '../../shared/flowTokens';
import type { GovernanceNodeData, FlowNodeData } from '../../types/flowchart.types';

const W = T.node.width;
const H = T.node.height;

function GovernanceNodeComponent({ id, data, selected }: NodeProps) {
  const d = data as unknown as GovernanceNodeData;
  const { updateNodeData } = useReactFlow();
  const [editValue, setEditValue] = useState(d.label);
  const inputRef = useRef<HTMLInputElement>(null);
  const editing = !!(d as any).editing;

  useEffect(() => {
    if (editing) {
      setEditValue(d.label);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [editing, d.label]);

  const saveEdit = useCallback(() => {
    updateNodeData(id, { label: editValue, editing: false } as Partial<FlowNodeData>);
  }, [id, editValue, updateNodeData]);

  const cancelEdit = useCallback(() => {
    updateNodeData(id, { editing: false } as Partial<FlowNodeData>);
  }, [id, updateNodeData]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      e.stopPropagation(); // Prevent React Flow from capturing keys (Backspace, Delete, etc.)
      if (e.key === 'Enter') saveEdit();
      if (e.key === 'Escape') cancelEdit();
    },
    [saveEdit, cancelEdit],
  );

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <NodeResizer
        minWidth={60}
        minHeight={30}
        isVisible={!!selected && !!(d as any).resizeEnabled}
        lineClassName="node-resizer-line"
        handleClassName="node-resizer-handle"
      />
      <Handle
        type="target"
        position={Position.Top}
        id="top-t"
        style={{
          background: T.color.navyText,
          width: 4,
          height: 4,
          border: `1px solid ${T.color.navy}`,
        }}
      />
      <Handle
        type="source"
        position={Position.Top}
        id="top-s"
        style={{ background: 'transparent', width: 10, height: 10, border: 'none' }}
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id="bottom-t"
        style={{
          background: T.color.navyText,
          width: 4,
          height: 4,
          border: `1px solid ${T.color.navy}`,
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom-s"
        style={{ background: 'transparent', width: 10, height: 10, border: 'none' }}
      />
      <Handle
        type="target"
        position={Position.Right}
        id="right-t"
        style={{
          background: T.color.navyText,
          width: 4,
          height: 4,
          border: `1px solid ${T.color.navy}`,
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right-s"
        style={{ background: 'transparent', width: 10, height: 10, border: 'none' }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left-t"
        style={{
          background: T.color.navyText,
          width: 4,
          height: 4,
          border: `1px solid ${T.color.navy}`,
        }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left-s"
        style={{ background: 'transparent', width: 10, height: 10, border: 'none' }}
      />

      <svg width="100%" height="100%" preserveAspectRatio="none" viewBox={`0 0 ${W} ${H}`}>
        <rect
          x={T.shape.strokeWidthNavy / 2}
          y={T.shape.strokeWidthNavy / 2}
          width={W - T.shape.strokeWidthNavy}
          height={H - T.shape.strokeWidthNavy}
          rx={T.shape.borderRadius}
          ry={T.shape.borderRadius}
          fill={T.color.navy}
          stroke={T.color.navyDark}
          strokeWidth={T.shape.strokeWidthNavy}
        />
        {/* Icono de escudo — lado izquierdo */}
        <g transform="translate(6, 9)">
          <path
            d="M5 0.5L1 2.5v3c0 2.5 1.7 4.3 4 5 2.3-.7 4-2.5 4-5v-3L5 0.5z"
            stroke={T.color.navyText}
            strokeWidth={0.6}
            fill="none"
          />
          <path
            d="M3.5 5l1 1L6.5 4"
            stroke={T.color.navyText}
            strokeWidth={0.6}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </g>
      </svg>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: T.font.family,
          pointerEvents: editing ? 'auto' : 'none',
          paddingLeft: 18,
          paddingRight: 4,
          boxShadow: selected ? `0 0 0 2px ${T.color.nodeStrokeSelected}` : undefined,
          borderRadius: T.shape.borderRadius,
        }}
      >
        {editing ? (
          <input
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={saveEdit}
            style={{
              fontSize: T.font.nodeTitle.size,
              fontWeight: T.font.nodeTitle.weight,
              fontFamily: T.font.family,
              color: T.color.navyText,
              textAlign: 'center',
              border: 'none',
              outline: 'none',
              background: 'transparent',
              width: '100%',
              padding: 0,
            }}
          />
        ) : (
          <span
            style={{
              fontSize: T.font.nodeTitle.size,
              fontWeight: T.font.nodeTitle.weight,
              color: T.color.navyText,
              textAlign: 'center',
              lineHeight: 1.2,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
            }}
            title={d.label}
          >
            {d.label}
          </span>
        )}
      </div>
    </div>
  );
}

export const GovernanceNode = memo(GovernanceNodeComponent);
